import { useRef, useState } from 'react';
import { MAP_MAX_BYTES, type MapData, extractMapData, plural } from '@quadro/domain';
import { Sheet, SheetFooter } from '../../components/Sheet';
import { hud } from '../../components/Hud';
import * as api from '../../data/api';
import { keys, useAction, useTasks } from '../../data/queries';
import { closeSheet } from '../../state/sheets';
import { useUI } from '../../state/ui';
import { useMapSource } from '../shared/useMap';
import { clearLayoutCache } from './layoutCache';
import { useMapState } from './mapState';

/** Importa o HTML/JSON do mapa do fechamento (alternativa ao mapa ao vivo do banco). */
export function ImportMapSheet() {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<{ name: string; data: MapData } | null>(null);
  const [error, setError] = useState('');
  const tasks = useTasks().data ?? [];
  const { source } = useMapSource();
  const save = useAction(api.importMap, [keys.fileMap]);
  const ids = new Set(file?.data.nos.map(n => n.id));
  const orphans = file ? tasks.filter(t => t.entityIds.some(id => !ids.has(id))).length : 0;

  async function pick(f: File) {
    setError('');
    try {
      const data = extractMapData(await f.text());
      if (new Blob([JSON.stringify(data)]).size > MAP_MAX_BYTES) throw new Error('O mapa é grande demais para importar.');
      setFile({ name: f.name, data });
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <Sheet onClose={closeSheet} label="Importar mapa societário">
      <div className="sh-body"><h2>Importar mapa societário</h2>
        <p className="muted" style={{ margin: '-6px 0 12px' }}>Envie o arquivo HTML do mapa gerado no fechamento (ou o JSON dos dados). Ele fica disponível como fonte "Arquivo", ao lado do mapa ao vivo do banco.</p>
        <input ref={input} type="file" accept=".html,.htm,.json,text/html,application/json" onChange={e => { const f = e.target.files?.[0]; if (f) pick(f); }} />
        {error && <p style={{ margin: '10px 0 0', color: 'var(--red)' }}>{error}</p>}
        {file && (
          <>
            <p className="muted" style={{ margin: '12px 0 0' }}>Data-base <b>{file.data.dataBase || 'não informada'}</b>, {file.data.nos.length} entidades e {file.data.arestas.length} relações, do arquivo {file.name}.</p>
            {source?.kind === 'file' && <p className="muted" style={{ margin: '8px 0 0' }}>O arquivo atual (data-base {source.data.dataBase}) será substituído para toda a equipe. As tarefas mantêm as entidades pelo identificador.</p>}
            {orphans > 0 && <p style={{ margin: '10px 0 0', color: 'var(--red)' }}>{plural(orphans, 'tarefa cita', 'tarefas citam')} entidades que não existem neste mapa. Elas continuam ligadas, marcadas como fora do mapa.</p>}
          </>
        )}
      </div>
      <SheetFooter>
        <span className="grow" />
        <button className="btn" onClick={closeSheet}>Cancelar</button>
        <button className="btn primary" disabled={!file} onClick={async () => {
          if (!file) return;
          await save(file.name, file.data);
          clearLayoutCache();
          useUI.getState().setMap({ source: 'file', tab: 'mapa' });
          useMapState.getState().set({ selected: null, camera: null });
          closeSheet(); hud('Mapa importado');
        }}>Importar</button>
      </SheetFooter>
    </Sheet>
  );
}
